import {AttrsSchema, Component, jsx} from 'panel';
import ResizeObserver from 'resize-observer-polyfill';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {SvgIconEl} from 'common/components/svg-icon';

import css from './index.styl?inline';
import {MetricChartEl, MetricChartSize} from './metric';
import {MetricChartConversionStep, MetricSegment, MultiMetricChartAttributes, MultiMetricChartState} from './types';
import {getGridDimensions} from './utils';

export class MultiMetricChart extends Component<MultiMetricChartState, MultiMetricChartAttributes> {
  private selfResizeObserver: ResizeObserver;

  static get attrsSchema(): AttrsSchema<MultiMetricChartAttributes> {
    return {
      ...super.attrsSchema,
      metrics: {type: `json`, tsType: `MetricSegment[]`},
      steps: {type: `json`, tsType: `MetricChartConversionStep[]`},
    };
  }

  get config() {
    return {
      useShadowDom: true,
      css,
      template: () => this.renderTemplate(),
      defaultState: {
        selfWidth: 0,
        selfHeight: 0,
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.selfResizeObserver = new ResizeObserver((entries) => {
      const {width, height} = entries[0].contentRect;
      this.update({selfWidth: width, selfHeight: height});
    });

    this.selfResizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.selfResizeObserver.disconnect();
    super.disconnectedCallback();
  }

  getMetricSize() {
    // adjust as needed for different metric sizes
    const minHeightForLargeSize = 200;
    const minWidthForLargeSize = 256;

    const {rows, columns} = getGridDimensions(this.state.selfWidth, this.attr(`metrics`).length);
    return this.state.selfHeight / rows >= minHeightForLargeSize &&
      this.state.selfWidth / columns >= minWidthForLargeSize
      ? MetricChartSize.Large
      : MetricChartSize.Small;
  }

  getMetricsToDisplay(): {gridMetrics: MetricSegment[]; bottomMetrics: MetricSegment[]} {
    const metrics = this.attr(`metrics`);
    const {rows, columns} = getGridDimensions(this.state.selfWidth, metrics.length);

    // only show as many metrics as allowed by the grid
    const maxAllowed = rows * columns;
    const truncatedMetrics = metrics.slice(0, maxAllowed);

    // render bottom row as separate grid in cases that there is an odd number of metrics
    // this allows us to fill up the remaining space
    const numBottomMetrics =
      maxAllowed === truncatedMetrics.length ? 0 : truncatedMetrics.length - columns * (rows - 1);
    const [gridMetrics, bottomMetrics] = [
      truncatedMetrics.slice(0, truncatedMetrics.length - numBottomMetrics),
      truncatedMetrics.slice(truncatedMetrics.length - numBottomMetrics),
    ];

    return {gridMetrics, bottomMetrics};
  }

  renderTemplate() {
    const metrics = this.attr(`metrics`);
    const {rows, columns} = getGridDimensions(this.state.selfWidth, metrics.length);

    const {gridMetrics, bottomMetrics} = this.getMetricsToDisplay();
    const metricSize = this.getMetricSize();

    const renderMetricChart = ({comparisonValue, label, value, subLabel, valueType, color}: MetricSegment) => {
      return (
        <div sel='.metric-container'>
          <MetricChartEl
            attrs={{
              'comparison-value': comparisonValue,
              'sub-label': subLabel,
              'value-type': valueType,
              size: metricSize,
              label,
              value,
              color,
            }}
          />
        </div>
      );
    };

    return (
      <div sel='.root'>
        {this.attr(`steps`) && this.renderStepsHeader()}
        <div
          sel='.grid'
          style={{
            'grid-template-columns': `repeat(${columns}, 1fr)`,
            'grid-template-rows': `repeat(${rows}, 1fr)`,
          }}
        >
          {gridMetrics.map(renderMetricChart)}
          {bottomMetrics.length ? (
            <div sel='.bottom-row' style={{'grid-template-columns': `repeat(${bottomMetrics.length}, 1fr)`}}>
              {bottomMetrics.map(renderMetricChart)}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  renderStepsHeader() {
    const [step1, step2] = this.attr(`steps`);
    const renderStep = (step: MetricChartConversionStep) => <div sel='.step'>{`${step.label}. ${step.name}`}</div>;

    return (
      <div sel='.steps-header'>
        {renderStep(step1)} <SvgIconEl attrs={{icon: `arrow-right`}} />
        {renderStep(step2)}
      </div>
    );
  }
}

interface MultiMetricChartJsxProps {
  attrs: MultiMetricChartAttributes;
}

export const MultiMetricChartEl = defineAndMakeJsxFactory<MultiMetricChartJsxProps>(
  `mp-multi-metric`,
  MultiMetricChart,
);
